import { getLogger, uuid } from '@signalwire/core';
import { getUserMedia, getMediaConstraints, filterIceServers, } from './utils/helpers';
import { sdpStereoHack, sdpBitrateHack, sdpMediaOrderHack, sdpHasValidCandidates, sdpHasCandidatesForEachMedia, } from './utils/sdpHelpers';
import { sdpToJsonHack, RTCPeerConnection, streamIsValid, stopTrack, } from './utils';
import { watchRTCPeerMediaPackets } from './utils/watchRTCPeerMediaPackets';
import { connectionPoolManager } from './connectionPoolManager';
const RESUME_TIMEOUT = 12000;
export default class RTCPeer {
    get logger() {
        return getLogger();
    }
    constructor(call, type) {
        this.call = call;
        this.type = type;
        this.uuid = uuid();
        this._negotiating = false;
        this._processingRemoteSDP = false;
        this._restartingIce = false;
        this._candidatesSnapshot = [];
        this._allCandidates = [];
        this._processingLocalSDP = false;
        this.logger.debug('New Peer with type:', this.type, 'Options:', this.options);
        this._onIce = this._onIce.bind(this);
        this._onEndedTrackHandler = this._onEndedTrackHandler.bind(this);
        if (this.options.prevCallId) {
            this.uuid = this.options.prevCallId;
        }
        this.options.prevCallId = undefined;
        if (this.options.localStream && streamIsValid(this.options.localStream)) {
            this._localStream = this.options.localStream;
        }
        this.rtcConfigPolyfill = this.config;
    }
    get options() {
        return this.call.options;
    }
    get watchMediaPacketsTimeout() {
        var _a;
        return (_a = this.options.watchMediaPacketsTimeout) !== null && _a !== void 0 ? _a : 2000;
    }
    get isNegotiating() {
        return this._negotiating;
    }
    get localStream() {
        return this._localStream;
    }
    set localStream(stream) {
        this._localStream = stream;
    }
    get remoteStream() {
        return this._remoteStream;
    }
    get isOffer() {
        return this.type === 'offer';
    }
    get isAnswer() {
        return this.type === 'answer';
    }
    get isSimulcast() {
        return this.options.simulcast === true;
    }
    get isSfu() {
        return this.options.sfu === true;
    }
    get localVideoTrack() {
        const videoSender = this._getSenderByKind('video');
        return (videoSender === null || videoSender === void 0 ? void 0 : videoSender.track) || null;
    }
    get localAudioTrack() {
        const audioSender = this._getSenderByKind('audio');
        return (audioSender === null || audioSender === void 0 ? void 0 : audioSender.track) || null;
    }
    get remoteVideoTrack() {
        const videoReceiver = this._getReceiverByKind('video');
        return (videoReceiver === null || videoReceiver === void 0 ? void 0 : videoReceiver.track) || null;
    }
    get remoteAudioTrack() {
        const audioReceiver = this._getReceiverByKind('audio');
        return (audioReceiver === null || audioReceiver === void 0 ? void 0 : audioReceiver.track) || null;
    }
    get hasAudioSender() {
        return this._getSenderByKind('audio') ? true : false;
    }
    get hasVideoSender() {
        return this._getSenderByKind('video') ? true : false;
    }
    get hasAudioReceiver() {
        return this._getReceiverByKind('audio') ? true : false;
    }
    get hasVideoReceiver() {
        return this._getReceiverByKind('video') ? true : false;
    }
    get config() {
        const { rtcPeerConfig = {} } = this.options;
        const config = Object.assign({ bundlePolicy: 'max-compat', iceServers: filterIceServers(this.call.iceServers, {
                disableUdpIceServers: this.options.disableUdpIceServers,
            }), 
            // @ts-ignore
            sdpSemantics: 'unified-plan' }, rtcPeerConfig);
        this.logger.debug('RTC config', config);
        return config;
    }
    get localSdp() {
        var _a, _b;
        return (_b = (_a = this.instance) === null || _a === void 0 ? void 0 : _a.localDescription) === null || _b === void 0 ? void 0 : _b.sdp;
    }
    get remoteSdp() {
        var _a, _b;
        return (_b = (_a = this.instance) === null || _a === void 0 ? void 0 : _a.remoteDescription) === null || _b === void 0 ? void 0 : _b.sdp;
    }
    get hasIceServers() {
        if (this.instance) {
            const { iceServers = [] } = this.getConfiguration();
            return Boolean(iceServers === null || iceServers === void 0 ? void 0 : iceServers.length);
        }
        return false;
    }
    stopTrackSender(kind) {
        var _a;
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender) {
                return this.logger.info(`There is not a '${kind}' sender to stop.`);
            }
            if (sender.track) {
                stopTrack(sender.track);
                (_a = this._localStream) === null || _a === void 0 ? void 0 : _a.removeTrack(sender.track);
            }
        }
        catch (error) {
            this.logger.error('RTCPeer stopTrackSender error', kind, error);
        }
    }
    stopTrackReceiver(kind) {
        var _a;
        try {
            const receiver = this._getReceiverByKind(kind);
            if (!receiver) {
                return this.logger.info(`There is not a '${kind}' receiver to stop.`);
            }
            if (receiver.track) {
                stopTrack(receiver.track);
                (_a = this._remoteStream) === null || _a === void 0 ? void 0 : _a.removeTrack(receiver.track);
            }
        }
        catch (error) {
            this.logger.error('RTCPeer stopTrackReceiver error', kind, error);
        }
    }
    async restoreTrackSender(kind) {
        var _a;
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender) {
                return this.logger.info(`There is not a '${kind}' sender to restore.`);
            }
            if (sender.track && sender.track.readyState !== 'ended') {
                return this.logger.info(`There is already an active ${kind} track.`);
            }
            const constraints = await getMediaConstraints(this.options);
            // @ts-ignore
            const stream = await getUserMedia({ [kind]: constraints[kind] });
            if (stream && streamIsValid(stream)) {
                const newTrack = stream.getTracks().find((t) => t.kind === kind);
                if (newTrack) {
                    await sender.replaceTrack(newTrack);
                    (_a = this._localStream) === null || _a === void 0 ? void 0 : _a.addTrack(newTrack);
                }
            }
        }
        catch (error) {
            this.logger.error('RTCPeer restoreTrackSender error', kind, error);
        }
    }
    getDeviceId(kind) {
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender || !sender.track) {
                return null;
            }
            const { deviceId = null } = sender.track.getSettings();
            return deviceId;
        }
        catch (error) {
            this.logger.error('RTCPeer getDeviceId error', kind, error);
            return null;
        }
    }
    getTrackSettings(kind) {
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender || !sender.track) {
                return null;
            }
            return sender.track.getSettings();
        }
        catch (error) {
            this.logger.error('RTCPeer getTrackSettings error', kind, error);
            return null;
        }
    }
    getTrackConstraints(kind) {
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender || !sender.track) {
                return null;
            }
            return sender.track.getConstraints();
        }
        catch (error) {
            this.logger.error('RTCPeer getTrackConstraints error', kind, error);
            return null;
        }
    }
    getDeviceLabel(kind) {
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender || !sender.track) {
                return null;
            }
            return sender.track.label;
        }
        catch (error) {
            this.logger.error('RTCPeer getDeviceLabel error', kind, error);
            return null;
        }
    }
    restartIceWithRelayOnly() {
        var _a, _b;
        try {
            if (this.isAnswer) {
                return this.logger.warn('Skip restartIceWithRelayOnly since we need to generate answer');
            }
            const config = this.getConfiguration();
            if (config.iceTransportPolicy === 'relay') {
                return this.logger.warn('RTCPeer already with iceTransportPolicy relay only');
            }
            const newConfig = Object.assign(Object.assign({}, config), { iceTransportPolicy: 'relay' });
            this.setConfiguration(newConfig);
            this.restartIce();
        }
        catch (error) {
            this.logger.error('restartIceWithRelayOnly', error);
            (_a = this._rejectStartMethod) === null || _a === void 0 ? void 0 : _a.call(this, error);
            (_b = this._pendingNegotiationPromise) === null || _b === void 0 ? void 0 : _b.reject(error);
        }
    }
    restartIce() {
        if (this._negotiating || this._restartingIce) {
            return this.logger.warn('Skip restartIce');
        }
        this._restartingIce = true;
        this.logger.debug('Restart ICE');
        // Type must be Offer to send reinvite.
        this.type = 'offer';
        this.instance.restartIce();
    }
    triggerResume() {
        this.logger.info('Probably half-open so force close from client');
        if (this._resumeTimer) {
            this.logger.info('[skipped] Already in "resume" state');
            return;
        }
        this.call.emit('media.disconnected');
        this.call.emit('media.reconnecting');
        this.clearTimers();
        this._resumeTimer = setTimeout(() => {
            this.logger.warn('Disconnecting due to RECONNECTION_ATTEMPT_TIMEOUT');
            this.call.emit('media.disconnected');
            this.call.leaveReason = 'RECONNECTION_ATTEMPT_TIMEOUT';
            this.call.setState('hangup');
        }, RESUME_TIMEOUT); // TODO: read from call verto.invite response
        this.call._closeWSConnection();
    }
    resetNeedResume() {
        this.clearResumeTimer();
        if (this.options.watchMediaPackets) {
            this.startWatchMediaPackets();
        }
    }
    stopWatchMediaPackets() {
        if (this._mediaWatcher) {
            this._mediaWatcher.stop();
        }
    }
    startWatchMediaPackets() {
        var _a;
        this.stopWatchMediaPackets();
        this._mediaWatcher = watchRTCPeerMediaPackets(this);
        (_a = this._mediaWatcher) === null || _a === void 0 ? void 0 : _a.start();
    }
    async applyMediaConstraints(kind, constraints) {
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender || !sender.track) {
                return this.logger.info('No sender to apply constraints', kind, constraints);
            }
            if (sender.track.readyState === 'live') {
                const newConstraints = Object.assign(Object.assign({}, sender.track.getConstraints()), constraints);
                const deviceId = this.getDeviceId(kind);
                if (deviceId && !this.options.screenShare) {
                    newConstraints.deviceId = { exact: deviceId };
                }
                this.logger.info(`Apply ${kind} constraints`, this.call.id, newConstraints);
                // Should we check if the current track is capable enough to support the incoming constraints?
                // https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/getCapabilities
                await sender.track.applyConstraints(newConstraints);
            }
        }
        catch (error) {
            this.logger.error('Error applying constraints', kind, constraints);
        }
    }
    _getSenderByKind(kind) {
        var _a;
        if (!((_a = this.instance) === null || _a === void 0 ? void 0 : _a.getSenders)) {
            this.logger.warn('RTCPeerConnection.getSenders() not available.');
            return null;
        }
        return this.instance
            .getSenders()
            .find(({ track }) => track && track.kind === kind);
    }
    _getReceiverByKind(kind) {
        var _a;
        if (!((_a = this.instance) === null || _a === void 0 ? void 0 : _a.getReceivers)) {
            this.logger.warn('RTCPeerConnection.getReceivers() not available.');
            return null;
        }
        return this.instance
            .getReceivers()
            .find(({ track }) => track && track.kind === kind);
    }
    async startNegotiation(force = false) {
        var _a, _b, _c;
        if (this._negotiating) {
            return this.logger.warn('Skip twice onnegotiationneeded!');
        }
        this._negotiating = true;
        try {
            /**
             * additionalDevice and screenShare are `sendonly`
             */
            if (this.options.additionalDevice || this.options.screenShare) {
                (_b = (_a = this.instance) === null || _a === void 0 ? void 0 : _a.getTransceivers) === null || _b === void 0 ? void 0 : _b.call(_a).forEach((tr) => {
                    tr.direction = 'sendonly';
                });
            }
            this.instance.removeEventListener('icecandidate', this._onIce);
            this.instance.addEventListener('icecandidate', this._onIce);
            if (this.isOffer) {
                this.logger.debug('Trying to generate offer');
                const offerOptions = {
                    /**
                     * While this property is deprected, on Browsers where this
                     * is still supported this avoids conflicting with the VAD
                     * server-side
                     */
                    // @ts-ignore
                    voiceActivityDetection: false,
                };
                if (!this._supportsAddTransceiver()) {
                    offerOptions.offerToReceiveAudio = this.options.negotiateAudio;
                    offerOptions.offerToReceiveVideo = this.options.negotiateVideo;
                }
                const offer = await this.instance.createOffer(offerOptions);
                await this._setLocalDescription(offer);
            }
            if (this.isAnswer) {
                this.logger.debug('Trying to generate answer');
                await this._setRemoteDescription({
                    sdp: this.options.remoteSdp,
                    type: 'offer',
                });
                const answer = await this.instance.createAnswer({
                    // Same as above.
                    // @ts-ignore
                    voiceActivityDetection: false,
                });
                await this._setLocalDescription(answer);
            }
            /**
             * ReactNative and Early invite Workaround
             */
            if (force && this.instance.signalingState === 'have-local-offer') {
                this._sdpReady();
            }
            this.logger.info('iceGatheringState', this.instance.iceGatheringState);
            if (this.instance.iceGatheringState === 'gathering') {
                this._iceTimeout = setTimeout(() => {
                    this._onIceTimeout();
                }, this.options.maxIceGatheringTimeout);
            }
        }
        catch (error) {
            this.logger.error(`Error creating ${this.type}:`, error);
            (_c = this._pendingNegotiationPromise) === null || _c === void 0 ? void 0 : _c.reject(error);
        }
    }
    onRemoteBye({ code, message }) {
        var _a, _b;
        // It could be a negotiation/signaling error so reject the "startMethod"
        const error = { code, message };
        (_a = this._rejectStartMethod) === null || _a === void 0 ? void 0 : _a.call(this, error);
        (_b = this._pendingNegotiationPromise) === null || _b === void 0 ? void 0 : _b.reject(error);
        this.stop();
    }
    async onRemoteSdp(sdp) {
        var _a, _b;
        if (this._processingRemoteSDP ||
            (this.remoteSdp && this.remoteSdp === sdp)) {
            this.logger.warn('Ignore same remote SDP', sdp);
            return;
        }
        try {
            const type = this.isOffer ? 'answer' : 'offer';
            if (type === 'answer' &&
                this.instance.signalingState !== 'have-local-offer') {
                this.logger.warn('Ignoring offer SDP as signaling state is not have-local-offer');
                return;
            }
            this._processingRemoteSDP = true;
            await this._setRemoteDescription({ sdp, type });
            this._processingRemoteSDP = false;
            /**
             * Resolve the start() method only for Offer because for Answer
             * we need to reply to the server and wait for the signaling.
             */
            if (this.isOffer) {
                this._resolveStartMethod();
                (_a = this._pendingNegotiationPromise) === null || _a === void 0 ? void 0 : _a.resolve();
            }
            this.resetNeedResume();
        }
        catch (error) {
            this._processingRemoteSDP = false;
            this.logger.error(`Error handling remote SDP on call ${this.call.id}:`, error);
            this.call.hangup();
            this._rejectStartMethod(error);
            (_b = this._pendingNegotiationPromise) === null || _b === void 0 ? void 0 : _b.reject(error);
        }
    }
    _setupRTCPeerConnection() {
        if (!this.instance) {
            // Try to get a pre-warmed connection from session-level pool
            let pooledConnection = null;
            try {
                pooledConnection = connectionPoolManager.getConnection();
            }
            catch (error) {
                this.logger.debug('Could not access session connection pool', error);
            }
            if (pooledConnection) {
                this.logger.info('Using pre-warmed connection from session pool with ICE candidates ready');
                this.instance = pooledConnection;
                // The connection is already clean:
                // - Mock tracks have been stopped and removed
                // - ICE candidates are gathered and ready
                // - TURN allocation is fresh
                // - All event listeners have been removed
            }
            else {
                // Fallback to creating new connection
                this.logger.debug('Creating new RTCPeerConnection (no pooled connection available)');
                this.instance = RTCPeerConnection(this.config);
            }
            this._attachListeners();
        }
    }
    async start() {
        return new Promise(async (resolve, reject) => {
            var _a, _b, _c;
            this._resolveStartMethod = resolve;
            this._rejectStartMethod = reject;
            try {
                this._localStream = await this._retrieveLocalStream();
            }
            catch (error) {
                this._rejectStartMethod(error);
                (_a = this._pendingNegotiationPromise) === null || _a === void 0 ? void 0 : _a.reject(error);
                return this.call.setState('hangup');
            }
            /**
             * We need to defer the creation of RTCPeerConnection
             * until we gain gUM access otherwise it will have
             * private IP addresses in ICE host candidates
             * replaced by an mDNS hostname
             * @see https://groups.google.com/g/discuss-webrtc/c/6stQXi72BEU?pli=1
             */
            this._setupRTCPeerConnection();
            let hasLocalTracks = false;
            if (this._localStream && streamIsValid(this._localStream)) {
                const audioTracks = this._localStream.getAudioTracks();
                this.logger.debug('Local audio tracks: ', audioTracks);
                const videoTracks = this._localStream.getVideoTracks();
                this.logger.debug('Local video tracks: ', videoTracks);
                hasLocalTracks = Boolean(audioTracks.length || videoTracks.length);
                // TODO: use transceivers way only for offer - when answer gotta match mid from the ones from SRD
                if (this.isOffer && this._supportsAddTransceiver()) {
                    const audioTransceiverParams = {
                        direction: this.options.negotiateAudio ? 'sendrecv' : 'sendonly',
                        streams: [this._localStream],
                    };
                    this.logger.debug('Applying audioTransceiverParams', audioTransceiverParams);
                    // Reuse existing audio transceivers from pooled connections
                    const existingAudioTransceivers = this.instance
                        .getTransceivers()
                        .filter((t) => {
                        var _a, _b;
                        return ((_a = t.receiver.track) === null || _a === void 0 ? void 0 : _a.kind) === 'audio' ||
                            (!t.sender.track &&
                                !t.receiver.track &&
                                ((_b = t.mid) === null || _b === void 0 ? void 0 : _b.includes('audio')));
                    });
                    audioTracks.forEach((track, index) => {
                        var _a;
                        if (index < existingAudioTransceivers.length) {
                            // Reuse existing transceiver
                            const transceiver = existingAudioTransceivers[index];
                            this.logger.debug('Reusing existing audio transceiver', transceiver.mid);
                            transceiver.sender.replaceTrack(track);
                            transceiver.direction =
                                audioTransceiverParams.direction || 'sendrecv';
                            // Add stream association
                            if ((_a = audioTransceiverParams.streams) === null || _a === void 0 ? void 0 : _a[0]) {
                                // @ts-ignore - streams is a valid property but not in TS types
                                transceiver.sender.streams = audioTransceiverParams.streams;
                            }
                        }
                        else {
                            // Create new transceiver only if needed
                            this.logger.debug('Creating new audio transceiver');
                            this.instance.addTransceiver(track, audioTransceiverParams);
                        }
                    });
                    const videoTransceiverParams = {
                        direction: this.options.negotiateVideo ? 'sendrecv' : 'sendonly',
                        streams: [this._localStream],
                    };
                    if (this.isSimulcast) {
                        const rids = ['0', '1', '2'];
                        videoTransceiverParams.sendEncodings = rids.map((rid) => ({
                            active: true,
                            rid: rid,
                            scaleResolutionDownBy: Number(rid) * 6 || 1.0,
                        }));
                    }
                    this.logger.debug('Applying videoTransceiverParams', videoTransceiverParams);
                    // Reuse existing video transceivers from pooled connections
                    const existingVideoTransceivers = this.instance
                        .getTransceivers()
                        .filter((t) => {
                        var _a, _b;
                        return ((_a = t.receiver.track) === null || _a === void 0 ? void 0 : _a.kind) === 'video' ||
                            (!t.sender.track &&
                                !t.receiver.track &&
                                ((_b = t.mid) === null || _b === void 0 ? void 0 : _b.includes('video')));
                    });
                    videoTracks.forEach((track, index) => {
                        var _a;
                        if (index < existingVideoTransceivers.length) {
                            // Reuse existing transceiver
                            const transceiver = existingVideoTransceivers[index];
                            this.logger.debug('Reusing existing video transceiver', transceiver.mid);
                            transceiver.sender.replaceTrack(track);
                            transceiver.direction =
                                videoTransceiverParams.direction || 'sendrecv';
                            // Add stream association
                            if ((_a = videoTransceiverParams.streams) === null || _a === void 0 ? void 0 : _a[0]) {
                                // @ts-ignore - streams is a valid property but not in TS types
                                transceiver.sender.streams = videoTransceiverParams.streams;
                            }
                            // Apply simulcast encodings if needed
                            if (videoTransceiverParams.sendEncodings) {
                                const params = transceiver.sender.getParameters();
                                params.encodings = videoTransceiverParams.sendEncodings;
                                transceiver.sender.setParameters(params);
                            }
                        }
                        else {
                            // Create new transceiver only if needed
                            this.logger.debug('Creating new video transceiver');
                            this.instance.addTransceiver(track, videoTransceiverParams);
                        }
                    });
                    if (this.isSfu) {
                        const { msStreamsNumber = 5 } = this.options;
                        this.logger.debug('Add ', msStreamsNumber, 'recvonly MS Streams');
                        videoTransceiverParams.direction = 'recvonly';
                        for (let i = 0; i < Number(msStreamsNumber); i++) {
                            this.instance.addTransceiver('video', videoTransceiverParams);
                        }
                    }
                }
                else if (typeof this.instance.addTrack === 'function') {
                    // Use addTrack
                    // To avoid TS complains in forEach
                    const stream = this._localStream;
                    audioTracks.forEach((track) => this.instance.addTrack(track, stream));
                    videoTracks.forEach((track) => this.instance.addTrack(track, stream));
                }
                else {
                    // Fallback to legacy addStream ..
                    // @ts-ignore
                    this.instance.addStream(this._localStream);
                }
            }
            if (this.isOffer) {
                // Handle unused transceivers from pooled connections
                if (this.instance.signalingState === 'have-local-offer') {
                    // We're reusing a pooled connection
                    this.logger.debug('Reusing pooled connection, managing transceivers');
                    // Get local tracks to determine what transceivers we need
                    const localAudioTracks = ((_b = this._localStream) === null || _b === void 0 ? void 0 : _b.getAudioTracks()) || [];
                    const localVideoTracks = ((_c = this._localStream) === null || _c === void 0 ? void 0 : _c.getVideoTracks()) || [];
                    // Set unused transceivers to inactive
                    const transceivers = this.instance.getTransceivers();
                    transceivers.forEach((transceiver) => {
                        var _a, _b, _c, _d;
                        const isAudioTransceiver = ((_a = transceiver.receiver.track) === null || _a === void 0 ? void 0 : _a.kind) === 'audio' ||
                            (!transceiver.sender.track &&
                                !transceiver.receiver.track &&
                                ((_b = transceiver.mid) === null || _b === void 0 ? void 0 : _b.includes('audio')));
                        const isVideoTransceiver = ((_c = transceiver.receiver.track) === null || _c === void 0 ? void 0 : _c.kind) === 'video' ||
                            (!transceiver.sender.track &&
                                !transceiver.receiver.track &&
                                ((_d = transceiver.mid) === null || _d === void 0 ? void 0 : _d.includes('video')));
                        // If we don't have audio tracks and this is an audio transceiver, set to inactive
                        if (isAudioTransceiver && localAudioTracks.length === 0) {
                            this.logger.debug('Setting unused audio transceiver to inactive', transceiver.mid);
                            transceiver.direction = 'inactive';
                        }
                        // If we don't have video tracks and this is a video transceiver, set to inactive
                        if (isVideoTransceiver && localVideoTracks.length === 0) {
                            this.logger.debug('Setting unused video transceiver to inactive', transceiver.mid);
                            transceiver.direction = 'inactive';
                        }
                    });
                }
                if (this.options.negotiateAudio) {
                    this._checkMediaToNegotiate('audio');
                }
                if (this.options.negotiateVideo) {
                    this._checkMediaToNegotiate('video');
                }
                if (this.instance.signalingState === 'have-local-offer') {
                    // we are reusing a pooled connection
                    this.logger.debug('Reusing pooled connection with local offer');
                    this.startNegotiation(true);
                }
                /**
                 * If it does not support unified-plan stuff (senders/receivers/transceivers)
                 * invoke manually startNegotiation and use the RTCOfferOptions
                 */
                if (!this._supportsAddTransceiver() && !hasLocalTracks) {
                    this.startNegotiation();
                }
            }
            else {
                this.startNegotiation();
            }
        });
    }
    detachAndStop() {
        var _a;
        if (typeof ((_a = this.instance) === null || _a === void 0 ? void 0 : _a.getTransceivers) === 'function') {
            this.instance.getTransceivers().forEach((transceiver) => {
                // Do not use `stopTrack` util to not dispatch the `ended` event
                if (transceiver.sender.track) {
                    transceiver.sender.track.stop();
                }
                if (transceiver.receiver.track) {
                    transceiver.receiver.track.stop();
                }
            });
        }
        this.stop();
    }
    stop() {
        var _a, _b, _c;
        // Do not use `stopTrack` util to not dispatch the `ended` event
        (_a = this._localStream) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((track) => track.stop());
        (_b = this._remoteStream) === null || _b === void 0 ? void 0 : _b.getTracks().forEach((track) => track.stop());
        (_c = this.instance) === null || _c === void 0 ? void 0 : _c.close();
        this.stopWatchMediaPackets();
    }
    _supportsAddTransceiver() {
        return typeof this.instance.addTransceiver === 'function';
    }
    _checkMediaToNegotiate(kind) {
        // addTransceiver of 'kind' if not present
        const sender = this._getSenderByKind(kind);
        if (!sender && this._supportsAddTransceiver()) {
            // Check if we already have a transceiver for this kind (from pooled connection)
            const existingTransceiver = this.instance
                .getTransceivers()
                .find((t) => {
                var _a, _b;
                return ((_a = t.receiver.track) === null || _a === void 0 ? void 0 : _a.kind) === kind ||
                    (!t.sender.track && !t.receiver.track && ((_b = t.mid) === null || _b === void 0 ? void 0 : _b.includes(kind)));
            });
            if (existingTransceiver) {
                this.logger.debug('Found existing transceiver for', kind, existingTransceiver.mid);
                // Update direction if needed
                if (existingTransceiver.direction === 'inactive' ||
                    existingTransceiver.direction === 'sendonly') {
                    existingTransceiver.direction = 'recvonly';
                }
            }
            else {
                const transceiver = this.instance.addTransceiver(kind, {
                    direction: 'recvonly',
                });
                this.logger.debug('Add transceiver', kind, transceiver);
            }
        }
    }
    async _sdpReady() {
        var _a, _b;
        if (this._processingLocalSDP) {
            this.logger.debug('Already processing local SDP, skipping');
            return;
        }
        // Check if we're still in the right state
        if (this.type === 'offer' &&
            !['have-local-offer', 'have-local-pranswer'].includes(this.instance.signalingState)) {
            // the local SDP was processed already and onnegotiationneeded was not fired
            // this happens because there are multiple places calling _sdpReady
            this.logger.warn(`_sdpReady called in wrong state: ${this.instance.signalingState}`);
            return;
        }
        this._processingLocalSDP = true;
        clearTimeout(this._iceTimeout);
        if (!this.instance.localDescription) {
            this.logger.error('Missing localDescription', this.instance);
            return;
        }
        const { sdp } = this.instance.localDescription;
        if (!sdpHasCandidatesForEachMedia(sdp)) {
            this.logger.info('No candidate - retry \n');
            this._processingLocalSDP = false;
            this.startNegotiation(true);
            return;
        }
        if (!this._sdpIsValid()) {
            this.logger.info('SDP ready but not valid');
            this._processingLocalSDP = false;
            this._onIceTimeout();
            return;
        }
        try {
            await this.call.onLocalSDPReady(this);
            this._processingLocalSDP = false;
            if (this.isAnswer) {
                this._resolveStartMethod();
                (_a = this._pendingNegotiationPromise) === null || _a === void 0 ? void 0 : _a.resolve();
            }
        }
        catch (error) {
            this._rejectStartMethod(error);
            (_b = this._pendingNegotiationPromise) === null || _b === void 0 ? void 0 : _b.reject(error);
            this._processingLocalSDP = false;
        }
    }
    _sdpIsValid() {
        if (this.localSdp && this.hasIceServers) {
            return sdpHasValidCandidates(this.localSdp);
        }
        return Boolean(this.localSdp);
    }
    _forceNegotiation() {
        this.logger.info('Force negotiation again');
        this._negotiating = false;
        this.startNegotiation();
    }
    _onIceTimeout() {
        var _a;
        if (this._sdpIsValid()) {
            this._sdpReady();
            return;
        }
        this.logger.info('ICE gathering timeout');
        const config = this.getConfiguration();
        if (config.iceTransportPolicy === 'relay') {
            this.logger.info('RTCPeer already with "iceTransportPolicy: relay"');
            const error = {
                code: 'ICE_GATHERING_FAILED',
                message: 'Ice gathering timeout',
            };
            this._rejectStartMethod(error);
            (_a = this._pendingNegotiationPromise) === null || _a === void 0 ? void 0 : _a.reject(error);
            this.call.setState('destroy');
            return;
        }
        this.setConfiguration(Object.assign(Object.assign({}, config), { iceTransportPolicy: 'relay' }));
        this._forceNegotiation();
    }
    _onIce(event) {
        var _a, _b;
        /**
         * Clear _iceTimeout on each single candidate
         */
        if (this._iceTimeout) {
            clearTimeout(this._iceTimeout);
        }
        /**
         * Following spec: no candidate means the gathering is completed.
         */
        if (!event.candidate) {
            this.instance.removeEventListener('icecandidate', this._onIce);
            // not call _sdpReady if an early invite has been sent
            if (this._candidatesSnapshot.length > 0) {
                this.logger.debug('No more candidates, calling _sdpReady');
                this._sdpReady();
            }
            return;
        }
        // Store all candidates
        this._allCandidates.push(event.candidate);
        this.logger.debug('RTCPeer Candidate:', event.candidate);
        if (event.candidate.type === 'host') {
            /**
             * With `host` candidate set timeout to
             * maxIceGatheringTimeout and then invoke
             * _onIceTimeout to check if the SDP is valid
             */
            this._iceTimeout = setTimeout(() => {
                this.instance.removeEventListener('icecandidate', this._onIce);
                this._onIceTimeout();
            }, this.options.maxIceGatheringTimeout);
        }
        else {
            /**
             * With non-HOST candidate (srflx, prflx or relay), check if we have
             * candidates for all media sections to support early invite
             */
            if ((_a = this.instance.localDescription) === null || _a === void 0 ? void 0 : _a.sdp) {
                if (sdpHasValidCandidates(this.instance.localDescription.sdp)) {
                    // Take a snapshot of candidates at this point
                    if (this._candidatesSnapshot.length === 0 && this.type === 'offer') {
                        this._candidatesSnapshot = [...this._allCandidates];
                        this.logger.info('SDP has candidates for all media sections, calling _sdpReady for early invite');
                        setTimeout(() => this._sdpReady(), 0); // Defer to allow any pending operations to complete
                    }
                }
                else {
                    this.logger.info('SDP does not have candidates for all media sections, waiting for more candidates');
                    this.logger.debug((_b = this.instance.localDescription) === null || _b === void 0 ? void 0 : _b.sdp);
                }
            }
        }
    }
    _retryWithMoreCandidates() {
        // Check if we have better candidates now than when we first sent SDP
        const hasMoreCandidates = this._hasMoreCandidates();
        if (hasMoreCandidates && this.instance.connectionState !== 'connected') {
            this.logger.info('More candidates found after ICE gathering complete, triggering renegotiation');
            // Reset negotiation state to allow new negotiation
            this._negotiating = false;
            this._candidatesSnapshot = [];
            this._allCandidates = [];
            // set the SDP type to 'offer' since the client is initiating a new negotiation
            this.type = 'offer';
            // Start negotiation with force=true
            if (this.instance.signalingState === 'stable') {
                this.startNegotiation(true);
            }
            else {
                this.logger.warn('Signaling state is not stable, cannot start negotiation immediately');
                this.restartIce();
            }
        }
    }
    _hasMoreCandidates() {
        return this._allCandidates.length > this._candidatesSnapshot.length;
    }
    _setLocalDescription(localDescription) {
        const { useStereo, googleMaxBitrate, googleMinBitrate, googleStartBitrate, } = this.options;
        if (localDescription.sdp && useStereo) {
            localDescription.sdp = sdpStereoHack(localDescription.sdp);
        }
        if (localDescription.sdp &&
            googleMaxBitrate &&
            googleMinBitrate &&
            googleStartBitrate) {
            localDescription.sdp = sdpBitrateHack(localDescription.sdp, googleMaxBitrate, googleMinBitrate, googleStartBitrate);
        }
        // this.logger.debug(
        //   'LOCAL SDP \n',
        //   `Type: ${localDescription.type}`,
        //   '\n\n',
        //   localDescription.sdp
        // )
        return this.instance.setLocalDescription(localDescription);
    }
    _setRemoteDescription(remoteDescription) {
        if (remoteDescription.sdp && this.options.useStereo) {
            remoteDescription.sdp = sdpStereoHack(remoteDescription.sdp);
        }
        if (remoteDescription.sdp && this.instance.localDescription) {
            remoteDescription.sdp = sdpMediaOrderHack(remoteDescription.sdp, this.instance.localDescription.sdp);
        }
        const sessionDescr = sdpToJsonHack(remoteDescription);
        this.logger.debug('REMOTE SDP \n', `Type: ${remoteDescription.type}`, '\n\n', remoteDescription.sdp);
        return this.instance.setRemoteDescription(sessionDescr);
    }
    async _retrieveLocalStream() {
        if (streamIsValid(this.options.localStream)) {
            return this.options.localStream;
        }
        const constraints = await getMediaConstraints(this.options);
        return getUserMedia(constraints);
    }
    _attachListeners() {
        this.instance.addEventListener('signalingstatechange', () => {
            this.logger.debug('signalingState:', this.instance.signalingState);
            switch (this.instance.signalingState) {
                case 'stable':
                    // Workaround to skip nested negotiations
                    // Chrome bug: https://bugs.chromium.org/p/chromium/issues/detail?id=740501
                    this._negotiating = false;
                    this._restartingIce = false;
                    this.resetNeedResume();
                    if (this.instance.connectionState === 'connected') {
                        // An ice restart won't change the connectionState so we emit the same event in here
                        // since the signalingState is "stable" again.
                        this.emitMediaConnected();
                    }
                    break;
                case 'have-local-offer': {
                    if (this.instance.iceGatheringState === 'complete') {
                        this.instance.removeEventListener('icecandidate', this._onIce);
                        this._sdpReady();
                    }
                    break;
                }
                // case 'have-remote-offer': {}
                case 'closed':
                    // @ts-ignore
                    delete this.instance;
                    break;
                default:
                    this._negotiating = true;
            }
        });
        this.instance.addEventListener('connectionstatechange', () => {
            this.logger.debug('connectionState:', this.instance.connectionState);
            switch (this.instance.connectionState) {
                // case 'new':
                //   break
                case 'connecting':
                    this._connectionStateTimer = setTimeout(() => {
                        this.logger.warn('connectionState timed out');
                        if (this._hasMoreCandidates()) {
                            this._retryWithMoreCandidates();
                        }
                        else {
                            this.restartIceWithRelayOnly();
                        }
                    }, this.options.maxConnectionStateTimeout);
                    break;
                case 'connected':
                    this.clearConnectionStateTimer();
                    this.emitMediaConnected();
                    break;
                // case 'closed':
                //   break
                case 'disconnected':
                    this.logger.debug('[test] Prevent reattach!');
                    break;
                case 'failed': {
                    this.triggerResume();
                    break;
                }
            }
        });
        this.instance.addEventListener('negotiationneeded', () => {
            this.logger.debug('Negotiation needed event');
            this.startNegotiation();
        });
        this.instance.addEventListener('iceconnectionstatechange', () => {
            this.logger.debug('iceConnectionState:', this.instance.iceConnectionState);
        });
        this.instance.addEventListener('icegatheringstatechange', () => {
            this.logger.debug('iceGatheringState:', this.instance.iceGatheringState);
            if (this.instance.iceGatheringState === 'complete') {
                this.logger.debug('ICE gathering complete');
                void this._sdpReady();
            }
        });
        // this.instance.addEventListener('icecandidateerror', (event) => {
        //   this.logger.warn('IceCandidate Error:', event)
        // })
        this.instance.addEventListener('track', (event) => {
            // @ts-expect-error
            this.call.emit('track', event);
            if (this.isSfu) {
                // const notification = { type: 'trackAdd', event }
                // this.call._dispatchNotification(notification)
            }
            this._remoteStream = event.streams[0];
        });
        // @ts-ignore
        this.instance.addEventListener('addstream', (event) => {
            if (event.stream) {
                this._remoteStream = event.stream;
            }
        });
        this._attachAudioTrackListener();
        this._attachVideoTrackListener();
    }
    clearTimers() {
        this.clearResumeTimer();
        this.clearWatchMediaPacketsTimer();
        this.clearConnectionStateTimer();
    }
    clearConnectionStateTimer() {
        clearTimeout(this._connectionStateTimer);
    }
    clearWatchMediaPacketsTimer() {
        clearTimeout(this._watchMediaPacketsTimer);
    }
    clearResumeTimer() {
        clearTimeout(this._resumeTimer);
        this._resumeTimer = undefined;
    }
    emitMediaConnected() {
        this.call.emit('media.connected');
    }
    _onEndedTrackHandler(event) {
        const mediaTrack = event.target;
        const evt = mediaTrack.kind === 'audio' ? 'microphone' : 'camera';
        this.call.emit(`${evt}.disconnected`, {
            deviceId: mediaTrack.id,
            label: mediaTrack.label,
        });
    }
    _attachAudioTrackListener() {
        var _a;
        (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.getAudioTracks().forEach((track) => {
            track.addEventListener('ended', this._onEndedTrackHandler);
        });
    }
    _attachVideoTrackListener() {
        var _a;
        (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.getVideoTracks().forEach((track) => {
            track.addEventListener('ended', this._onEndedTrackHandler);
        });
    }
    _detachAudioTrackListener() {
        var _a;
        (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.getAudioTracks().forEach((track) => {
            track.removeEventListener('ended', this._onEndedTrackHandler);
        });
    }
    _detachVideoTrackListener() {
        var _a;
        (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.getVideoTracks().forEach((track) => {
            track.removeEventListener('ended', this._onEndedTrackHandler);
        });
    }
    /**
     * React Native does not support getConfiguration
     * so we polyfill it using a local `rtcConfigPolyfill` object.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/setConfiguration#parameters
     */
    setConfiguration(config) {
        var _a;
        this.rtcConfigPolyfill = config;
        if (this.instance &&
            typeof ((_a = this.instance) === null || _a === void 0 ? void 0 : _a.setConfiguration) === 'function') {
            this.instance.setConfiguration(config);
        }
    }
    /**
     * React Native does not support getConfiguration
     * so we polyfill it using a local config object.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/getConfiguration
     */
    getConfiguration() {
        var _a;
        if (this.instance &&
            typeof ((_a = this.instance) === null || _a === void 0 ? void 0 : _a.getConfiguration) === 'function') {
            return this.instance.getConfiguration();
        }
        return this.rtcConfigPolyfill || this.config;
    }
}
//# sourceMappingURL=RTCPeer.js.map